import { type MutableRefObject, useCallback, useEffect, useState } from 'react';
import debounce from 'lodash/debounce';
import useResizeObserver from '@react-hook/resize-observer';

import { useMediaQueries } from '@tmh-website/shared';
import { parseCSSUnit } from '@tmh-website/styles/functions';
import theme from '@tmh-website/styles/theme';
import StyledTypo from '@tmh-website/styles/typo';

/**
 * Returns the length of a string in the parent Element
 * @param htmlString
 * @param parentEl
 * @returns The length in pixels
 */
function getStringLength(htmlString: string, parentEl: HTMLElement): number {
  const headlineEl = document.createElement('span');
  headlineEl.style.display = 'inline';
  headlineEl.style.position = 'absolute';
  headlineEl.style.pointerEvents = 'none';
  headlineEl.style.opacity = '0 !important';
  headlineEl.style.transition = 'none !important';
  headlineEl.innerHTML = htmlString;
  parentEl.appendChild(headlineEl);
  const length = headlineEl.clientWidth;
  headlineEl.remove();
  return length;
}

interface WordLengths {
  space: number;
  words: number[];
}

/**
 * Returns the length of each word as well as a space character in the parent element
 * This splits the words by space so it treats special characters as regular letters
 * and may not work with e.g. email adresses since they break differently.
 * @param text
 * @param parentElement The element where the text will be displayed
 * @returns A WordLengths object
 */
export function getLengthOfWords(text: string, parentElement: HTMLElement): WordLengths {
  return {
    space: getStringLength('&nbsp;', parentElement),
    words: text.split(' ').map(word => getStringLength(word, parentElement)),
  };
}

/**
 * Checks how long each line will be
 * @param wordLengths
 * @param parentElement
 * @returns Array with the length for each line
 */
export function getLengthOfLines(wordLengths: WordLengths, parentElementWidth: number): number[] {
  // Add words (with spaces in between) until they don't fit into the container anymore
  const lineLengths: number[] = [];
  const currentWords = [...wordLengths.words];
  for (let i = 0; currentWords.length > 0; i++) {
    lineLengths[i] = currentWords.shift()!; // Every line has at least one word even if it doesn't fit
    while (lineLengths[i] + wordLengths.space + currentWords[0] < parentElementWidth && currentWords.length > 0) {
      lineLengths[i] += wordLengths.space + currentWords.shift()!;
    }
  }
  return lineLengths;
}

export interface AnchorCoordinates {
  /**
   * The absolute x position of the anchor
   */
  x: number;
  /**
   * The absolute y position of the anchor
   */
  y: number;
}

export interface GetLineAnchorObject extends AnchorCoordinates {
  /**
   * The width of the AnimatedArrow if it would connect to the toPoint
   */
  width?: number;
  /**
   * The height of the AnimatedArrow if it would connect to the toPoint
   */
  height?: number;
}

interface GetLineAnchorOnTextOptions {
  /**
   * The text alignment. Used to determine if the arrow attaches on the left or the right
   */
  anchorPosition?: 'left' | 'right';
  /**
   * Provide a point where the anchor will connect to. This is used to also return the width and
   * height of the AnimatedArrow
   */
  toPoint?: AnchorCoordinates;
  /**
   * Which center line should be picked, when the text has an even number of lines?
   * Center means, that both center lines will be handled as a single one.
   */
  evenLineCountPreference?: 'top' | 'bottom' | 'center';
  /**
   * The gap to the last letter in pixels. Default is theme.gap.medium
   */
  gap?: string;
  /**
   * Limits how far the anchor point can be inside the element. This is usefull if you want to
   * prevent the AnimatedLine from going very far inside the headlin on weird line breaks.
   */
  insetLimit?: number;
  /**
   * The breakpoints on where the text size changes. Used to prevent unnecessary recalculation of
   * word sizes since those need to be meassured by injecting an element.
   * Only calculated once if the array is empty and on every resize when undefined.
   * Default is the default text breakpoints.
   */
  textBreakpoints?: number[];
  /**
   * The trottle timeout on the resize handler in ms.
   */
  resizeDebounce?: number;
}

const useGetLineAnchorOnTextDefaultOptions = {
  anchorPosition: 'right',
  insetLimit: 0.5,
  evenLineCountPreference: 'bottom',
  gap: theme.gap.large,
  textBreakpoints: StyledTypo.textBreakpoints,
  resizeDebounce: 100,
};

/**
 * Get the x and y position of where to attach the AnimatedLine on a given text.
 * Does not work with padding yet!
 * @param elementRef
 * @param options
 * @returns
 */
export function useGetLineAnchorOnText(
  /**
   * The text element where the anchor should attach
   */
  elementRef: MutableRefObject<HTMLElement | null>,
  options?: GetLineAnchorOnTextOptions
): GetLineAnchorObject | null {
  const [returnObject, setReturnObject] = useState<GetLineAnchorObject | null>(null);
  const [wordLengths, setWordLengths] = useState<WordLengths | undefined>();
  const [lineLengths, setLineLengths] = useState<number[] | undefined>();
  const [elementContentBox, setElementContentBox] = useState<ResizeObserverSize | undefined>();
  const [elementStyles, setElementStyles] = useState<CSSStyleDeclaration | undefined>();
  const [gapInPx, setGapInPx] = useState<number>(0);
  const fullOptions = { ...useGetLineAnchorOnTextDefaultOptions, ...options };

  // Store styles of element in state
  useEffect(() => {
    const currentEl = elementRef.current;
    if (currentEl) {
      setElementStyles(getComputedStyle(currentEl));
    }
  }, [elementRef.current]);

  // Adjust gap parameter to pixels
  useEffect(() => {
    if (fullOptions.gap) {
      setGapInPx(parseCSSUnit(fullOptions.gap));
    }
  }, [fullOptions.gap]);

  // Watch size changes of the target element
  const resizeHandler = (entry: ResizeObserverEntry) => {
    if (entry.contentBoxSize) {
      setElementContentBox(entry.contentBoxSize[0]);
    } else {
      // Fallback for Safari < v15.4
      setElementContentBox({
        blockSize: entry.target.scrollHeight,
        inlineSize: entry.target.scrollWidth,
      });
    }
  };
  const debouncedResizeHandler = useCallback(debounce(resizeHandler, fullOptions.resizeDebounce), []);
  useResizeObserver(elementRef.current, debouncedResizeHandler);

  // Watch text size changes
  const breakpoints: string[] = [];
  fullOptions.textBreakpoints?.forEach(textBreakpoint => {
    breakpoints.push(`(min-width: ${textBreakpoint}px)`);
  });
  const activeBreakpoints = JSON.stringify(useMediaQueries(breakpoints));

  // Get wordLengths on breakpoint change
  useEffect(() => {
    const currentEl = elementRef.current;
    if (currentEl) {
      setWordLengths(getLengthOfWords(currentEl.innerText, currentEl));
      setTimeout(() => {
        // We run this twice to make sure the wordLengths are correct if the font is loaded after the first run
        setWordLengths(getLengthOfWords(currentEl.innerText, currentEl));
      }, 1000);
    }
  }, [activeBreakpoints, elementRef.current]);

  // Get lineLengths on wordLengths change
  useEffect(() => {
    if (wordLengths && elementContentBox) {
      const elementWidth = elementContentBox.inlineSize;
      setLineLengths(getLengthOfLines(wordLengths, elementWidth));
    }
  }, [wordLengths, elementContentBox?.inlineSize]);

  // Use all the gathered information to calculate the x and y position including options
  useEffect(() => {
    if (!lineLengths || !elementContentBox || !elementStyles) {
      return;
    }

    // Get targetLineWidth and Y position within elementRef
    let targetLineWidth = 0;
    let yPos = 0;
    if (lineLengths.length % 2) {
      // Is uneven therefore pick the center line
      targetLineWidth = lineLengths[(lineLengths.length - 1) / 2];
      yPos = elementContentBox.blockSize / 2;
    } else if (fullOptions.evenLineCountPreference === 'top') {
      const lineIndex = lineLengths.length / 2 - 1;
      const singleLineHeight = elementContentBox.blockSize / lineLengths.length;
      targetLineWidth = lineLengths[lineIndex];
      yPos = singleLineHeight * lineIndex + singleLineHeight / 2;
    } else if (fullOptions.evenLineCountPreference === 'bottom') {
      const lineIndex = lineLengths.length / 2;
      const singleLineHeight = elementContentBox.blockSize / lineLengths.length;
      targetLineWidth = lineLengths[lineLengths.length / 2];
      yPos = singleLineHeight * lineIndex + singleLineHeight / 2;
    } else if (fullOptions.evenLineCountPreference === 'center') {
      const topLine = lineLengths[lineLengths.length / 2 - 1];
      const bottomLine = lineLengths[lineLengths.length / 2];
      targetLineWidth = Math.max(topLine, bottomLine);
      yPos = elementContentBox.blockSize / 2;
    }

    // Get x position based on text align an anchorPosition
    let xPos = 0;
    if (elementStyles.textAlign === 'left') {
      if (fullOptions.anchorPosition === 'left') {
        xPos = -gapInPx;
      } else {
        xPos = targetLineWidth + gapInPx;
      }
    } else if (elementStyles.textAlign === 'center') {
      let modifier = 0;
      if (fullOptions.anchorPosition === 'left') {
        modifier = -1;
      } else {
        modifier = 1;
      }
      xPos = elementContentBox.inlineSize / 2 + (targetLineWidth / 2 + gapInPx) * modifier;
    } else if (elementStyles.textAlign === 'right') {
      if (fullOptions.anchorPosition === 'left') {
        xPos = elementContentBox.inlineSize - targetLineWidth - gapInPx;
      } else {
        xPos = elementContentBox.inlineSize + gapInPx;
      }
    } else {
      throw new Error(`Invalid text-align property provided: ${elementStyles.textAlign}`);
    }

    // Set inset limit
    if (fullOptions.insetLimit) {
      if (!(fullOptions.insetLimit > 0 && fullOptions.insetLimit < 1)) {
        throw new Error(`The inset limit has to be between 0 and 1`);
      }
      if (fullOptions.anchorPosition === 'left') {
        xPos = Math.min(xPos, elementContentBox.inlineSize * fullOptions.insetLimit);
      } else if (fullOptions.anchorPosition === 'right') {
        xPos = Math.max(xPos, elementContentBox.inlineSize * fullOptions.insetLimit);
      }
    }

    // Calculate width and height if toPoint is set
    let width, height;
    if (fullOptions.toPoint) {
      const currentEl = elementRef.current;
      if (currentEl) {
        const currentElRect = currentEl.getBoundingClientRect();
        width = Math.abs(currentElRect.left + window.scrollX + xPos - fullOptions.toPoint.x);
        height = Math.abs(currentElRect.top + window.scrollY + yPos - fullOptions.toPoint.y);
      }
    }

    setReturnObject({ x: xPos, y: yPos, width: width, height: height });
  }, [lineLengths, elementContentBox, elementStyles, fullOptions.toPoint]);

  return returnObject;
}

export function removeHtmlLineBreaks(text: string): string {
  return text
    .replace(/&shy;/g, '')
    .replace(/<br\s*\/?>/g, ' ')
    .replace(/ {2} */g, ' ');
}

export function transformRichtextInput(
  input: string,
  {
    disableLinks = false,
    blurLinks = false,
    injectRequiredIndicator = false,
  }: { disableLinks?: boolean; blurLinks?: boolean; injectRequiredIndicator?: boolean }
): string {
  if (disableLinks) {
    // Remove links to prevent hydration mismatch when using richtext in articles summary
    // @ts-ignore - FIXME: fix regexp flag
    const regexp = /<a(?:[ \t\n\\n]*[A-z-]*?=\\?".*?\\?"[ \t\n\\n]*)*?>(.*?)<\/a(?:[ \t\n\\n]*?)?>/gms;
    input = input.replace(regexp, '$1');
  }

  if (blurLinks) {
    // Prevent links from being focusable (e.g. when the parent has aria-hidden="true")
    // @ts-ignore - FIXME: fix regexp flag
    const regexp = /<a([ \t\n\\n]*)/gms;
    input = input.replace(regexp, '<a tabindex="-1" $1');
  }

  if (injectRequiredIndicator) {
    // Add * just before last </p> tag, this is used for labels of required checkboxes
    if (/<\/p>\n?$/.test(input)) {
      input = input.replace(/<\/p>\n?$/, '*</p>');
    } else {
      input = `${input}*`;
    }
  }

  // Add container to tables for scrolling
  input = input.replace('<table', '<div style="overflow-x: auto;"><table').replace('</table>', '</table></div>');

  return input;
}
